import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

const getNotificationIcon = (category, notificationType) => {
  if (category === 'case' || notificationType === 'system') return { icon: 'Briefcase', color: 'text-blue-600', bg: 'bg-blue-50' };
  if (category === 'consultation' || notificationType === 'appointment') return { icon: 'Calendar', color: 'text-green-600', bg: 'bg-green-50' };
  if (category === 'document' || notificationType === 'document') return { icon: 'FileText', color: 'text-purple-600', bg: 'bg-purple-50' };
  if (notificationType === 'invoice') return { icon: 'Receipt', color: 'text-orange-600', bg: 'bg-orange-50' };
  if (notificationType === 'message') return { icon: 'MessageSquare', color: 'text-teal-600', bg: 'bg-teal-50' };
  return { icon: 'Bell', color: 'text-gray-600', bg: 'bg-gray-50' };
};

const NotificationItem = ({ notification, onRead, onNavigate }) => {
  const iconConfig = getNotificationIcon(notification?.category, notification?.notification_type);

  const handleClick = () => {
    if (!notification?.is_read) {
      onRead(notification?.id);
    }
    if (notification?.link) {
      onNavigate(notification?.link);
    }
  };

  const timeAgo = (() => {
    try {
      return formatDistanceToNow(new Date(notification?.created_at), { addSuffix: true });
    } catch {
      return '';
    }
  })();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => { if (e?.key === 'Enter') handleClick(); }}
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-secondary-50 focus:outline-none focus:bg-secondary-50 ${
        !notification?.is_read ? 'bg-primary-50 border-l-2 border-primary' : ''
      }`}
    >
      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${iconConfig?.bg}`}>
        <Icon name={iconConfig?.icon} size={16} className={iconConfig?.color} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${
            !notification?.is_read ? 'font-semibold text-text-primary' : 'font-medium text-text-primary'
          }`}>
            {notification?.title}
          </p>
          {!notification?.is_read && (
            <span className="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-primary" />
          )}
        </div>
        <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{notification?.message}</p>
        <p className="text-xs text-text-tertiary mt-1">{timeAgo}</p>
      </div>
    </div>
  );
};

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef?.current && !dropdownRef?.current?.contains(e?.target) &&
        buttonRef?.current && !buttonRef?.current?.contains(e?.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigate = (link) => {
    setIsOpen(false);
    navigate(link);
  };

  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(prev => !prev)}
        className="relative p-2 rounded-interactive text-text-secondary hover:text-text-primary hover:bg-secondary-50 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-error text-white text-[10px] font-bold rounded-full leading-none">
            {displayCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute right-0 top-full mt-2 w-80 bg-surface border border-border rounded-card shadow-modal z-dropdown"
          style={{ maxHeight: '480px', display: 'flex', flexDirection: 'column' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-heading-semibold text-text-primary">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 bg-primary text-white text-[10px] font-bold rounded-full">
                  {displayCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-primary hover:text-primary-700 font-medium transition-colors focus:outline-none"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mb-3">
                  <Icon name="BellOff" size={20} className="text-text-secondary" />
                </div>
                <p className="text-sm font-medium text-text-primary">No notifications yet</p>
                <p className="text-xs text-text-secondary mt-1">You'll see case updates, reminders, and document alerts here.</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map(notification => (
                  <NotificationItem
                    key={notification?.id}
                    notification={notification}
                    onRead={markAsRead}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications?.length > 0 && (
            <div className="px-4 py-2.5 border-t border-border flex-shrink-0">
              <p className="text-xs text-text-secondary text-center">
                Showing last {notifications?.length} notifications
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
